import { Injectable } from '@angular/core';

const MAX_PROFILE_BIND_RETRIES = 120;
// requestAnimationFrame runs ~60fps, so 120 retries ≈ 2 seconds.
// This avoids infinite retries while giving KWA enough time to hydrate.

@Injectable({
  providedIn: 'root'
})
export class StyleUtilService {
  removeSidebarOverflow(kwaSidebarElement: HTMLElement): void {
    const scrollableArea = kwaSidebarElement.shadowRoot?.querySelector(
      '.scrollable-area'
    ) as HTMLElement | null;

    if (scrollableArea) {
      scrollableArea.style.overflowY = 'unset';
    }
  }

  observeSidebarCollapse(
    platformNavDiv: HTMLElement,
    callback: (collapsed: boolean) => void
  ): MutationObserver {
    const observer = new MutationObserver(() => {
      callback(platformNavDiv.classList.contains('collapsed'));
    });

    observer.observe(platformNavDiv, {
      attributes: true,
      attributeFilter: ['class']
    });

    callback(platformNavDiv.classList.contains('collapsed'));
    return observer;
  }

  modifyKwaHeader(): void {
    const headerElement = document.querySelector('kwa-header') as HTMLElement | null;
    const shadowRoot = headerElement?.shadowRoot;
    if (!headerElement || !shadowRoot) return;

    const apply = () => {
      const root = headerElement.shadowRoot;
      if (!root) return;
      this.applyKwaHeaderPatches(root);
    };

    apply();

    if (!(headerElement as any).__kwaHeaderObserverBound) {
      (headerElement as any).__kwaHeaderObserverBound = true;

      const observer = new MutationObserver(() => apply());
      observer.observe(shadowRoot, { childList: true, subtree: true });
      (headerElement as any).__kwaHeaderObserver = observer;
    }

    if (!(headerElement as any).__kwaHeaderResizeBound) {
      (headerElement as any).__kwaHeaderResizeBound = true;

      const onResize = () => apply();
      window.addEventListener('resize', onResize, { passive: true });
      (headerElement as any).__kwaHeaderResizeHandler = onResize;
    }
  }

  bindKwaHeaderProfileClick(onClick: (anchorEl: HTMLElement) => void): void {
    const bindIfReady = (): boolean => {
      const headerElement = document.querySelector('kwa-header') as HTMLElement | null;
      const shadowRoot = headerElement?.shadowRoot;
      if (!shadowRoot) return false;

      this.applyKwaHeaderPatches(shadowRoot);

      const profileIcon = this.findProfileIcon(shadowRoot);
      if (!profileIcon) return false;

      if ((profileIcon as any).__profileClickBound) return true;
      (profileIcon as any).__profileClickBound = true;

      profileIcon.style.cursor = 'pointer';
      profileIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        onClick(profileIcon);
      });

      return true;
    };

    if (bindIfReady()) return;

    let tries = 0;

    const tick = () => {
      tries++;
      if (bindIfReady()) return;
      if (tries >= MAX_PROFILE_BIND_RETRIES) return;
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  setKwaHeaderProfileActive(active: boolean): void {
    const headerElement = document.querySelector('kwa-header') as HTMLElement | null;
    const shadowRoot = headerElement?.shadowRoot;
    if (!shadowRoot) return;

    const profileIcon = this.findProfileIcon(shadowRoot);
    if (!profileIcon) return;

    profileIcon.classList.toggle('logout-active', active);
  }

  setProfileActiveOnEl(anchorEl: HTMLElement, active: boolean): void {
    anchorEl.classList.toggle('logout-active', active);

    if (!active) {
      // Force repaint to avoid rare stale shadow artifacts after removing box-shadow
      anchorEl.style.setProperty('transform', 'translateZ(0)', 'important');
      requestAnimationFrame(() => {
        anchorEl.style.removeProperty('transform');
      });
    }
  }

  modifyGanttTooltipBadge(): void {
    const badgeElement = document.querySelector(
      '.study-gantt-chart-tooltip-status-item core-badge'
    ) as HTMLElement | null;

    if (!badgeElement) return;

    const shadowRoot = badgeElement.shadowRoot;
    if (!shadowRoot) return;

    const badgeContent = shadowRoot.querySelector('.core-badge') as HTMLElement | null;
    if (badgeContent) {
      badgeContent.style.fontWeight = '400';
    }
  }

  private applyKwaHeaderPatches(root: ShadowRoot): void {
    this.tagKwaHeaderIcons(root);
    this.hideKwaHeaderSeparator(root);

    this.forceUtilityVisible(root);
    this.nudgeProfileLeft(root);

    this.ensureIconVisibilityRules(root);
    this.ensureKwaHeaderProfileActiveStyles(root);
  }

  private tagKwaHeaderIcons(root: ShadowRoot): void {
    const icons = Array.from(root.querySelectorAll('.icon')) as HTMLElement[];

    icons.forEach((el) => {
      const svg = el.querySelector('svg') as SVGElement | null;
      const id = svg?.id ?? svg?.getAttribute('id') ?? '';

      if (id === 'profile-mono.svg') {
        el.setAttribute('data-kwa-icon', 'profile');
      } else if (id === 'hamburger-mono.svg') {
        el.setAttribute('data-kwa-icon', 'hamburger');
      } else if (id) {
        el.setAttribute('data-kwa-icon', id);
      } else {
        el.removeAttribute('data-kwa-icon');
      }
    });
  }

  private hideKwaHeaderSeparator(root: ShadowRoot): void {
    const sep = root.querySelector('.separator') as HTMLElement | null;
    if (sep) sep.style.display = 'none';
  }

  private forceUtilityVisible(root: ShadowRoot): void {
    const utility = root.querySelector('.header__container--utility') as HTMLElement | null;
    if (!utility) return;

    utility.style.setProperty('display', 'flex', 'important');
    utility.style.setProperty('visibility', 'visible', 'important');
    utility.style.setProperty('opacity', '1', 'important');
    utility.style.setProperty('pointer-events', 'auto', 'important');
    utility.style.setProperty('align-items', 'center', 'important');
  }

  private nudgeProfileLeft(root: ShadowRoot): void {
    const profileIcon = this.findProfileIcon(root);
    if (!profileIcon) return;

    profileIcon.style.setProperty('margin-right', '13px', 'important');
  }

  private ensureIconVisibilityRules(root: ShadowRoot): void {
    if (root.querySelector('style[data-kwa-icon-visibility="true"]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-kwa-icon-visibility', 'true');

    style.textContent = `
      .header__container .icon:not([data-kwa-icon="profile"]) {
        display: none !important;
      }

      .header__container .icon[data-kwa-icon="profile"] {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
      }
    `;

    root.appendChild(style);
  }

  private ensureKwaHeaderProfileActiveStyles(root: ShadowRoot): void {
    if (root.querySelector('style[data-profile-active-style="true"]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-profile-active-style', 'true');
    style.textContent = `
      .icon.logout-active {
        border-radius: 4px;
        background: var(--layer-hover-02, #DFEEFF);
        box-shadow: 0 0 0 1px var(--border-selected, #0071D0);
      }

      .icon:focus,
      .icon:focus-visible {
        outline: none !important;
        box-shadow: none !important;
      }
    `;

    root.appendChild(style);
  }

  private findProfileIcon(root: ShadowRoot): HTMLElement | null {
    const byAttr = root.querySelector('.icon[data-kwa-icon="profile"]') as HTMLElement | null;
    if (byAttr) return byAttr;

    const icons = Array.from(root.querySelectorAll('.icon')) as HTMLElement[];
    const profileIcon = icons.find((el) => {
      const svg = el.querySelector('svg') as SVGElement | null;
      const id = svg?.id ?? svg?.getAttribute('id') ?? '';
      return id === 'profile-mono.svg';
    });

    return profileIcon ?? null;
  }
}
